import { useState, useRef, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Upload, Combine, Rss, BookMarked, Library, StickyNote, AlertTriangle, ChevronRight } from 'lucide-react';
import { Dialog, Button, Input, ICON_SIZE } from '../design-system';
import { showErrorAlert, ErrorCategory } from '../lib/errorHandler';
import { CHROME_EXTENSION_URL } from '../utils/feedbackUrl';
import { getArticleCount } from '../store';
import { useSubscription } from '../hooks/useSubscription';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { getTierLimit, ARTICLE_STORAGE_SOFT_WARNING } from '@papper/shared/reedle/tiers';
import { openUsageModal } from '../store/ui$';
import './UploadModal.css';

export default function UploadModal({
  onClose,
  onAddArticle,
  onPdfUpload,
  onEpubUpload,
  onTextFileUpload,
  onAudioUpload,
  onOpenSnippet,
  onOpenBatchMerge,
  onOpenRssFeed,
  onOpenReadwise,
  onOpenZotero,
  uploadingPdf,
  uploadingEpub,
  uploadingAudio,
}) {
  const { t, i18n } = useTranslation('articles');
  const [urlInput, setUrlInput] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);
  // 焦点由 Dialog 的 initialFocusRef 在打开时给（替代 autoFocus 属性）。
  const urlInputRef = useRef(null);

  // PDF upload limit check
  const { usageCount: pdfUsageCount, limit: pdfLimit } = useFeatureAccess('pdf_upload');

  // Article storage limit check
  const { subscription, loading: subLoading } = useSubscription();
  const tier = subscription?.tier || 'free';
  const articleCount = useMemo(() => getArticleCount(), []);
  // Suppress limit banners while subscription is loading to avoid false "upgrade" gates.
  const articleLimit = subLoading ? null : getTierLimit(tier, 'articleStorage');
  const isAtLimit = articleLimit !== null && articleCount >= articleLimit;
  const isNearLimit = articleLimit !== null && articleCount >= ARTICLE_STORAGE_SOFT_WARNING && !isAtLimit;
  const supportedPlatforms = [
    'HTML',
    'x.com',
    ...(i18n.language === 'zh' ? ['zhihu.com', 'xiaohongshu'] : []),
  ].join(i18n.language === 'zh' || i18n.language === 'ja' ? '、' : ', ');
  const fileFormats = [
    pdfLimit !== null ? `PDF (${pdfUsageCount ?? 0}/${pdfLimit})` : 'PDF',
    'EPUB',
    'Markdown',
    'TXT',
    'Audio',
  ].join(' · ');

  // --- URL submission ---
  const handleUrlSubmit = (e) => {
    e?.preventDefault?.();
    if (!urlInput.trim()) return;
    if (isAtLimit) {
      openUsageModal('plan', 'article_storage');
      return;
    }
    onAddArticle(urlInput.trim());
    setUrlInput('');
  };

  // --- Unified file processing (supports multiple files, max 10) ---
  const processFile = (file) => {
    if (isAtLimit) {
      openUsageModal('plan', 'article_storage');
      return;
    }
    const name = file.name.toLowerCase();
    const type = file.type;

    if (type === 'application/pdf' || name.endsWith('.pdf')) {
      onPdfUpload(file);
    } else if (type === 'application/epub+zip' || name.endsWith('.epub')) {
      onEpubUpload(file);
    } else if (name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.markdown')) {
      onTextFileUpload(file);
    } else if (file.type.startsWith('audio/') || ['.mp3', '.m4a', '.wav', '.ogg'].some(ext => name.endsWith(ext))) {
      onAudioUpload(file);
    } else {
      showErrorAlert(
        t('uploadModal.unsupportedFormat'),
        null,
        { component: 'UploadModal', action: 'file_type_check', category: ErrorCategory.IMPORT }
      );
    }
  };

  const processFiles = (files) => {
    if (uploadingPdf || uploadingEpub || uploadingAudio) return;
    const batch = Array.from(files).slice(0, 10);
    for (const file of batch) {
      processFile(file);
    }
  };

  // --- Drag-and-drop handlers ---
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (!files?.length) return;
    processFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = '';
    processFiles(files);
  };

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      title={t('uploadModal.title')}
      size="lg"
      className="upload-modal"
      initialFocusRef={urlInputRef}
    >
      <div className="upload-modal-body">
        {/* Article storage limit warning */}
        {isAtLimit && (
          <div className="upload-modal-limit-banner limit-error">
            <AlertTriangle size={ICON_SIZE.xs} />
            <span>{t('uploadModal.limitReached', { articleCount, articleLimit })} <button type="button" className="upload-modal-link-btn" onClick={() => openUsageModal('plan', 'article_storage')}>{t('uploadModal.upgradeToPro')}</button> {t('uploadModal.forMoreStorage')}</span>
          </div>
        )}
        {isNearLimit && (
          <div className="upload-modal-limit-banner limit-warning">
            <AlertTriangle size={ICON_SIZE.xs} />
            <span>{t('uploadModal.approachingLimit', { articleCount, articleLimit })}</span>
          </div>
        )}
        <h3 className="upload-modal-section-title upload-modal-section-title--first">{t('uploadModal.directAddTitle')}</h3>
        <div className="upload-modal-url-row">
          <Input
            ref={urlInputRef}
            type="text"
            inputMode="url"
            autoComplete="off"
            className="upload-modal-url-input"
            aria-label={t('input.placeholder')}
            placeholder={t('input.placeholder')}
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleUrlSubmit(e); }}
          />
          <Button variant="primary" disabled={!urlInput.trim()} onClick={handleUrlSubmit}>
            {t('input.add')}
          </Button>
        </div>
        <p className="upload-modal-platforms">
          {t('uploadModal.supportedPlatforms')} {supportedPlatforms} {t('uploadModal.andMore')}{' '}
          <Trans
            i18nKey="uploadModal.extensionHint"
            ns="articles"
            components={{
              ext: (
                <a
                  className="upload-modal-extension-link"
                  href={CHROME_EXTENSION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </p>
        <div
          className={`upload-modal-drop-zone ${isDragActive ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          aria-label={t('uploadModal.dragOrClick')}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <Upload size={ICON_SIZE.md} className="upload-modal-drop-icon" />
          <p className="upload-modal-drop-text">
            {isDragActive
              ? t('uploadModal.dropHere')
              : t('uploadModal.dragOrClick')}
          </p>
          <p className="upload-modal-drop-formats">{fileFormats}</p>
          {(uploadingPdf || uploadingEpub || uploadingAudio) && (
            <p className="upload-modal-uploading">{t('input.uploading')}</p>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".txt,.md,.markdown,.pdf,.epub,text/plain,text/markdown,application/pdf,application/epub+zip,audio/*,.mp3,.m4a,.wav,.ogg"
          className="upload-modal-file-input"
          tabIndex={-1}
          onChange={handleFileSelect}
        />

        <button type="button" className="upload-modal-source upload-modal-source--direct" onClick={() => { onClose(); onOpenSnippet(); }}>
          <StickyNote size={ICON_SIZE.md} />
          <span><span className="upload-modal-source-title">{t('uploadModal.pasteTextAction')}</span><span className="upload-modal-source-description">{t('uploadModal.snippetDesc')}</span></span>
          <ChevronRight size={ICON_SIZE.sm} className="upload-modal-source-chevron" aria-hidden="true" />
        </button>

        <h3 className="upload-modal-section-title">{t('uploadModal.otherSourcesTitle')}</h3>
        <div className="upload-modal-sources">
          <button type="button" className="upload-modal-source" onClick={() => { onClose(); onOpenRssFeed(); }}>
            <Rss size={ICON_SIZE.md} />
            <span><span className="upload-modal-source-title">{t('uploadModal.rssFeed')}</span><span className="upload-modal-source-description">{t('uploadModal.rssDesc')}</span></span>
            <ChevronRight size={ICON_SIZE.sm} className="upload-modal-source-chevron" aria-hidden="true" />
          </button>

          <button type="button" className="upload-modal-source" onClick={() => { onClose(); onOpenReadwise(); }}>
            <BookMarked size={ICON_SIZE.md} />
            <span><span className="upload-modal-source-title">Readwise</span><span className="upload-modal-source-description">{t('uploadModal.readwiseDesc')}</span></span>
            <ChevronRight size={ICON_SIZE.sm} className="upload-modal-source-chevron" aria-hidden="true" />
          </button>

          <button type="button" className="upload-modal-source" onClick={() => { onClose(); onOpenZotero(); }}>
            <Library size={ICON_SIZE.md} />
            <span><span className="upload-modal-source-title">Zotero</span><span className="upload-modal-source-description">{t('uploadModal.zoteroDesc')}</span></span>
            <ChevronRight size={ICON_SIZE.sm} className="upload-modal-source-chevron" aria-hidden="true" />
          </button>

          <button type="button" className="upload-modal-source" onClick={() => { onClose(); onOpenBatchMerge(); }}>
            <Combine size={ICON_SIZE.md} />
            <span><span className="upload-modal-source-title">{t('input.merge')}</span><span className="upload-modal-source-description">{t('uploadModal.mergeDesc')}</span></span>
            <ChevronRight size={ICON_SIZE.sm} className="upload-modal-source-chevron" aria-hidden="true" />
          </button>
        </div>
      </div>
    </Dialog>
  );
}
